export interface BoundedQueue<T = unknown> {
    /**
     * Enqueues a task to be executed in the bounded queue.
     * If the queue is full, the oldest task waiting to be processed will be discarded.
     *
     * @param task The task to enqueue.
     * @returns A promise that resolves with the task's result or undefined if the task was discarded.
     */
    enqueue(task: (() => Promise<T>) | undefined): Promise<T | undefined>;

    /**
     * Clears all pending tasks in the queue.
     * Resolves all pending tasks with undefined.
     */
    clear(): void;
}

type TaskEntry<T = unknown> = {
    task: () => Promise<T>;
    resolve: (value: T | undefined) => void;
};

/**
 * Creates a bounded queue that limits the number of tasks that can be enqueued at a given time, to two items.
 * Enqueued tasks are executed sequentially. If new tasks are added while an enqueued task is waiting to be processed,
 * the item that is waiting to be processed will be overridden by the next enqueued task.
 */
export function createBoundedQueue<T = unknown>(): BoundedQueue<T> {
    let tasks: Array<TaskEntry<T>> = [];

    const enqueue = (task: (() => Promise<T>) | undefined): Promise<T | undefined> => {
        if (!task) {
            return Promise.resolve(undefined);
        }

        return new Promise((resolve) => {
            const taskEntry: TaskEntry<T> = {task, resolve};

            // If queue non empty then we assume a queue processor is active
            const shouldStartQueueProcessor = tasks.length === 0;

            // Only allow one additional task at given time
            const currentTask = tasks.at(0);
            if (currentTask) {
                // This is in essence, what makes this a bounded queue. Prevents
                // more than two items being queued, discards any item that was
                // awaiting execution. Resolve the kicked out task.
                const kickedOutTask = tasks.at(1);
                if (kickedOutTask) {
                    kickedOutTask.resolve(undefined);
                }
                tasks = [currentTask, taskEntry];
            } else {
                tasks = [taskEntry];
            }

            if (shouldStartQueueProcessor) {
                (async () => {
                    // TODO: move ui progress start here
                    while (tasks.length > 0) {
                        const peekTaskEntry = tasks.at(0);
                        if (peekTaskEntry) {
                            try {
                                const result = await peekTaskEntry.task();
                                peekTaskEntry.resolve(result);
                            } catch (error) {
                                peekTaskEntry.resolve(undefined);
                            }
                        }
                        // Actually remove the current task from the queue
                        tasks.shift();
                    }

                    // TODO: move ui complete here
                })();
            }
        });
    };

    const clear = () => {
        for (const taskEntry of tasks) {
            taskEntry.resolve(undefined);
        }
        tasks = [];
    };

    return {
        enqueue,
        clear,
    };
}
